import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Briefcase, Users, ShieldCheck, X, Sparkles, AlertCircle } from 'lucide-react';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PublicTopBar() {
  return (
    <header className="border-b border-[var(--vh-border)] bg-[var(--vh-bg-base)]/85 backdrop-blur sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-tight text-[var(--vh-brand-primary)]" data-testid="companies-brand">
          <span className="inline-flex w-8 h-8 rounded-md bg-[var(--vh-brand-primary)] text-white items-center justify-center text-sm font-bold">V</span>
          Vault Hire
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-[var(--vh-text-secondary)]">
          <Link to="/jobs" className="hover:text-[var(--vh-brand-primary)]">Jobs board</Link>
          <Link to="/companies" className="text-[var(--vh-brand-primary)] font-semibold">Companies</Link>
          <Link to="/trust" className="hover:text-[var(--vh-brand-primary)]">Trust</Link>
          <Link to="/pricing" className="hover:text-[var(--vh-brand-primary)]">Pricing</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 rounded-full text-sm text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)]">Sign in</Link>
          <Link to="/register" className="px-4 py-2 rounded-full text-sm bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)]">Get started</Link>
        </div>
      </div>
    </header>
  );
}

export default function CompaniesDirectory({ user }) {
  const [items, setItems] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [activeSector, setActiveSector] = useState('');
  const [memberships, setMemberships] = useState({});
  const [loading, setLoading] = useState(true);
  const [joinTarget, setJoinTarget] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = activeSector ? `${API}/companies/directory?sector=${encodeURIComponent(activeSector)}` : `${API}/companies/directory`;
      const [dir, sec] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/companies/sectors`),
      ]);
      setItems(dir.data.items);
      setSectors(sec.data);
      if (user?.role === 'candidate') {
        const mine = await axios.get(`${API}/me/talent-pool-memberships`);
        const map = {};
        for (const m of mine.data) map[m.employer_user_id] = m;
        setMemberships(map);
      }
    } catch (err) {
      logger.error('directory load', err);
    } finally { setLoading(false); }
  }, [activeSector, user]);

  useEffect(() => { load(); }, [load]);

  const handleJoin = (company) => {
    if (!user) { navigate('/register?redirect=/companies'); return; }
    if (user.role !== 'candidate') { return; }
    setJoinTarget(company);
  };

  const onJoined = () => {
    setJoinTarget(null);
    load();
  };

  const leave = async (employerUserId) => {
    if (!window.confirm('Leave this talent pool? Your AI profile will no longer be visible to them.')) return;
    try {
      await axios.delete(`${API}/companies/${employerUserId}/talent-pool/leave`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const chipBase = 'px-3 py-1.5 text-xs rounded-full border transition';
  const chipOn = 'bg-[var(--vh-brand-primary)] text-white border-[var(--vh-brand-primary)]';
  const chipOff = 'bg-[var(--vh-bg-surface)] text-[var(--vh-text-secondary)] border-[var(--vh-border)] hover:bg-[var(--vh-bg-muted)]';

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] text-[var(--vh-text-primary)]" data-testid="companies-directory">
      <PublicTopBar />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-3">Verified companies hiring on Vault Hire</p>
            <h1 className="font-display text-4xl md:text-5xl leading-[1.06] mb-3" data-testid="directory-title">
              Join a talent pool — your identity stays yours.
            </h1>
            <p className="text-base text-[var(--vh-text-secondary)] leading-relaxed">
              Companies see your AI-generated profile first. Your name and contact details are revealed only when they express interest.
            </p>
          </div>
          {!user && (
            <Link to="/register" className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)] text-sm font-semibold shadow-sm" data-testid="directory-cta-register">
              Create candidate account →
            </Link>
          )}
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 pb-20">
        {sectors.length > 0 && (
          <div className="mb-8 flex gap-2 flex-wrap" data-testid="sector-filters">
            <button
              onClick={() => setActiveSector('')}
              data-testid="sector-all-btn"
              className={`${chipBase} ${activeSector === '' ? chipOn : chipOff}`}
            >
              All sectors ({items.length})
            </button>
            {sectors.map((s) => (
              <button
                key={s.sector}
                onClick={() => setActiveSector(s.sector)}
                data-testid={`sector-btn-${s.sector}`}
                className={`${chipBase} ${activeSector === s.sector ? chipOn : chipOff}`}
              >
                {s.sector} ({s.count})
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-44 rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-[var(--vh-text-muted)]" data-testid="directory-empty">
            <Building2 className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No companies are open to talent pool joins yet. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((c) => {
              const isMember = !!memberships[c.employer_user_id] && memberships[c.employer_user_id].status === 'active';
              return (
                <article
                  key={c.employer_user_id}
                  data-testid={`company-card-${c.employer_user_id}`}
                  className="p-6 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl shadow-sm flex flex-col hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-lg text-[var(--vh-text-primary)] truncate">{c.company_name}</h3>
                      {c.sector && <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">{c.sector}</p>}
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-[var(--vh-brand-primary)] bg-[var(--vh-bg-muted)] px-2 py-1 rounded-full">
                      <ShieldCheck className="w-3 h-3" aria-hidden="true" /> Verified
                    </span>
                  </div>
                  {c.blurb && <p className="text-sm text-[var(--vh-text-secondary)] mb-4 line-clamp-3 leading-relaxed">{c.blurb}</p>}
                  <div className="flex items-center gap-4 text-xs text-[var(--vh-text-muted)] mt-auto mb-4">
                    <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" aria-hidden="true" /> {c.open_jobs} open</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" aria-hidden="true" /> {c.pool_size} in pool</span>
                  </div>
                  {isMember ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-[var(--vh-success)] font-semibold" data-testid={`member-badge-${c.employer_user_id}`}>✓ In pool</span>
                      <button
                        onClick={() => leave(c.employer_user_id)}
                        data-testid={`leave-btn-${c.employer_user_id}`}
                        className="text-xs text-[var(--vh-accent)] hover:underline"
                      >
                        Leave
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleJoin(c)}
                      data-testid={`join-btn-${c.employer_user_id}`}
                      className="w-full py-2.5 text-sm rounded-full bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)] transition flex items-center justify-center gap-1.5 font-semibold"
                    >
                      <Sparkles className="w-4 h-4" aria-hidden="true" /> Join talent pool
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </main>

      {joinTarget && (
        <JoinModal
          company={joinTarget}
          onClose={() => setJoinTarget(null)}
          onJoined={onJoined}
        />
      )}
    </div>
  );
}

function JoinModal({ company, onClose, onJoined }) {
  const [consent, setConsent] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/vault/ai-profile`)
      .then((r) => {
        if (cancelled) return;
        const approved = r.data?.approved_version
          || (Array.isArray(r.data?.versions) ? r.data.versions.find((p) => p.status === 'approved') : null);
        setProfile(approved);
      })
      .catch((err) => { setError(err.response?.data?.detail || 'Could not load your AI profile.'); })
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/companies/${company.employer_user_id}/talent-pool/join`, {
        consent_acknowledged: true,
      });
      onJoined(company.employer_user_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--vh-brand-primary)]/40 backdrop-blur-sm" role="dialog" aria-label={`Join ${company.company_name} talent pool`}>
      <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-xl w-full max-w-lg border border-[var(--vh-border)]" data-testid="join-modal">
        <header className="px-6 py-4 border-b border-[var(--vh-border)] flex items-center justify-between">
          <h2 className="font-display text-lg text-[var(--vh-text-primary)]">Join {company.company_name}&apos;s talent pool</h2>
          <button onClick={onClose} aria-label="Close" className="text-[var(--vh-text-muted)] hover:text-[var(--vh-text-primary)]">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-6 space-y-4">
          {loading ? (
            <div className="h-32 bg-[var(--vh-bg-muted)] animate-pulse rounded-xl" />
          ) : !profile ? (
            <div className="p-4 rounded-xl bg-[var(--vh-warning)]/30 border border-[var(--vh-warning)] text-[var(--vh-text-primary)] text-sm flex gap-2" data-testid="no-profile-warning">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-[var(--vh-accent)]" />
              <div>
                <p className="font-semibold">You need an approved AI Profile first.</p>
                <p className="mt-1 text-[var(--vh-text-secondary)]">Head to <Link to="/candidate/dashboard" className="underline text-[var(--vh-brand-primary)]">your vault</Link> to generate and approve one. It takes about 30 seconds.</p>
              </div>
            </div>
          ) : (
            <>
              <div>
                <h4 className="text-xs uppercase tracking-[0.15em] text-[var(--vh-accent)] mb-2">Profile that will be shared</h4>
                <div className="p-4 rounded-xl bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] text-xs text-[var(--vh-text-secondary)] max-h-44 overflow-y-auto whitespace-pre-wrap leading-relaxed" data-testid="profile-preview">
                  {(profile.content || '').slice(0, 1200)}{(profile.content || '').length > 1200 ? '…' : ''}
                </div>
                <p className="mt-2 text-xs text-[var(--vh-text-muted)]">Approved AI Profile · version {profile.version}</p>
              </div>

              <div className="p-4 rounded-xl bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] text-xs text-[var(--vh-text-secondary)] space-y-2 leading-relaxed">
                <p><strong className="text-[var(--vh-text-primary)]">What {company.company_name} will see:</strong> your AI profile only — no name, email, or contact details.</p>
                <p><strong className="text-[var(--vh-text-primary)]">What happens if they&apos;re interested:</strong> they tap &quot;Express interest&quot; and your name is revealed to them. You&apos;ll get a notification.</p>
                <p><strong className="text-[var(--vh-text-primary)]">Your control:</strong> you can leave the pool at any time. Consent expires after 12 months unless you renew.</p>
              </div>

              <label className="flex gap-2 items-start text-sm text-[var(--vh-text-secondary)]">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="consent-checkbox"
                  className="mt-1 accent-[var(--vh-accent)]"
                />
                <span>I consent to {company.company_name} viewing my AI profile under GDPR Art. 6(1)(a). I can withdraw at any time.</span>
              </label>

              {error && (
                <div className="text-xs text-[var(--vh-accent)]" data-testid="join-error">{error}</div>
              )}
            </>
          )}
        </div>
        <footer className="p-5 border-t border-[var(--vh-border)] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-full text-[var(--vh-text-secondary)] hover:bg-[var(--vh-bg-muted)]">Cancel</button>
          <button
            onClick={submit}
            disabled={!consent || !profile || busy}
            data-testid="confirm-join-btn"
            className="px-5 py-2 text-sm rounded-full bg-[var(--vh-accent)] text-white hover:bg-[var(--vh-accent-hover)] disabled:opacity-40 transition font-semibold"
          >
            {busy ? 'Joining…' : 'Confirm & join'}
          </button>
        </footer>
      </div>
    </div>
  );
}
